import { useState, useEffect, useCallback } from "react";
import { axiosInstance } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Package, Phone, Mail, MapPin, Truck, CheckCircle, XCircle, Clock, ShoppingBag, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "../utils/errorHandler";

const fmt = (n) => `₡${Number(n || 0).toLocaleString()}`;

// Build a wa.me URL that opens WhatsApp with the message pre-filled. Uses
// the customer's phone if present (digits only) and the appropriate text
// depending on the order's current status. No API/cost involved.
const buildWaUrl = (order) => {
  const phone = String(order.customer_phone || "").replace(/\D/g, "");
  if (!phone) return null;
  const cr = phone.startsWith("506") ? phone : `506${phone}`;
  const num = order.order_number;
  const total = `₡${Number(order.final_total ?? order.total ?? 0).toLocaleString()}`;
  let msg = "";
  if (order.status === "cancelled") {
    msg = `Hola ${order.customer_name}, te avisamos que tu pedido ${num} ha sido cancelado. Si tienes dudas escríbenos.`;
  } else if (order.status === "confirmed") {
    msg = order.delivery_method === "delivery"
      ? `Hola ${order.customer_name}, tu pedido ${num} está listo y saldrá pronto a entrega. Total: ${total}. ¡Gracias por tu compra!`
      : `Hola ${order.customer_name}, tu pedido ${num} ya está listo para retirar. Total: ${total}. ¡Te esperamos!`;
  } else if (order.sent_to_cashier_at) {
    msg = `Hola ${order.customer_name}, recibimos tu pedido ${num} y lo estamos preparando. Total estimado: ${total}.`;
  } else {
    msg = `Hola ${order.customer_name}, gracias por tu pedido ${num}. Lo estamos revisando y te avisamos cuando esté listo.`;
  }
  return `https://wa.me/${cr}?text=${encodeURIComponent(msg)}`;
};

const STATUS_LABEL = {
  pending: { txt: "Pendiente", color: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock },
  confirmed: { txt: "Confirmado", color: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle },
  cancelled: { txt: "Cancelado", color: "bg-red-100 text-red-800 border-red-300", icon: XCircle },
};

const STATUS_OPTIONS = [
  { value: "pending", label: "Pendiente" },
  { value: "confirmed", label: "Confirmado" },
  { value: "cancelled", label: "Cancelado" },
];

export default function Orders({ user }) {
  const [boxes, setBoxes] = useState([]);
  const [selectedBox, setSelectedBox] = useState(null);
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [actionId, setActionId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [discountPct, setDiscountPct] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");

  // Reset discount when opening a different order
  useEffect(() => { setDiscountPct(""); setDiscountAmount(""); }, [selectedOrder?.id]);

  const fetchBoxes = useCallback(async () => {
    try {
      const r = await axiosInstance.get("/boxes");
      setBoxes(r.data || []);
      if (!selectedBox && r.data?.length) {
        // Default to user's assigned box if cashier, else first
        const assigned = user?.role === "cajero" && user?.assigned_box_id
          ? r.data.find((b) => b.id === user.assigned_box_id)
          : r.data[0];
        setSelectedBox(assigned || r.data[0]);
      }
    } catch (e) { /* silent */ }
  }, [selectedBox, user]);

  const fetchOrders = useCallback(async () => {
    if (!selectedBox) return;
    setLoading(true);
    try {
      const params = { box_id: selectedBox.id };
      if (statusFilter !== "all") params.status = statusFilter;
      const r = await axiosInstance.get("/orders", { params });
      setOrders(r.data.orders || []);
      setPendingCount(r.data.pending_count || 0);
    } catch (e) {
      toast.error(getErrorMessage(e, "Error cargando pedidos"));
    } finally {
      setLoading(false);
    }
  }, [selectedBox, statusFilter]);

  useEffect(() => { fetchBoxes(); }, [fetchBoxes]);
  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Send the order to the Inventory cart so the cashier completes the sale
  // through the standard inventory flow (discount, payment method, receipt).
  // We stash the order payload in localStorage; Inventory.js reads it on mount.
  // We also persist the "sent to cashier" state on the backend so the order
  // appears as "Pendiente por facturar" on every device until it is actually
  // facturado (or recalled).
  const sendToInventory = async (order) => {
    const dPct = parseFloat(discountPct) || 0;
    const dAmt = parseFloat(discountAmount) || 0;
    const payload = {
      order_id: order.id,
      order_number: order.order_number,
      box_id: order.box_id,
      customer_name: order.customer_name,
      customer_phone: order.customer_phone,
      delivery_method: order.delivery_method,
      payment_intent: order.payment_intent,
      discount_percentage: dPct > 0 ? dPct : 0,
      discount_amount: dAmt > 0 ? dAmt : 0,
      items: (order.items || []).map((i) => ({
        product_id: i.product_id, code: i.product_code, name: i.product_name,
        color: i.color, talla: i.talla, quantity: i.quantity, price: i.price,
      })),
    };
    setActionId(order.id);
    try {
      // Persist the "sent to cashier" state + discount on the backend
      await axiosInstance.post(`/orders/${order.id}/send-to-cashier`, {
        discount_percentage: dPct > 0 ? dPct : 0,
        discount_amount: dAmt > 0 ? dAmt : 0,
      });
      try { localStorage.setItem("palistore_pending_order", JSON.stringify(payload)); } catch (e) { /* ignore */ }
      toast.success(`Pedido ${order.order_number} cargado en la caja. Termina la venta para confirmarlo.`);
      window.location.href = "/inventory";
    } catch (e) {
      toast.error(getErrorMessage(e, "No se pudo enviar a caja"));
    } finally {
      setActionId(null);
    }
  };

  // Recover an order that was sent to the cashier without facturando it.
  const recallFromCashier = async (orderId) => {
    if (!window.confirm("¿Recuperar el pedido del carrito de caja? El pedido volverá al estado de pendiente normal y deberá enviarse otra vez.")) return;
    setActionId(orderId);
    try {
      await axiosInstance.post(`/orders/${orderId}/recall-from-cashier`);
      // Clear localStorage too — best-effort, won't hurt if it wasn't there
      try {
        const raw = localStorage.getItem("palistore_pending_order");
        if (raw) {
          const data = JSON.parse(raw);
          if (data?.order_id === orderId) localStorage.removeItem("palistore_pending_order");
        }
      } catch (_) { /* ignore */ }
      toast.success("Pedido devuelto a la cola");
      setSelectedOrder(null);
      fetchOrders();
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al recuperar pedido"));
    } finally {
      setActionId(null);
    }
  };

  const confirmOrder = async (orderId) => {
    const dPct = parseFloat(discountPct) || 0;
    const dAmt = parseFloat(discountAmount) || 0;
    const hasDiscount = dPct > 0 || dAmt > 0;
    if (!window.confirm(hasDiscount
      ? `¿Confirmar con descuento (${dAmt > 0 ? `₡${dAmt}` : dPct + "%"})? Se descontará del stock y se registrará como venta.`
      : "¿Confirmar pedido? Se descontará del stock y se registrará como venta.")) return;
    setActionId(orderId);
    try {
      const body = {};
      if (dAmt > 0) body.discount_amount = dAmt;
      else if (dPct > 0) body.discount_percentage = dPct;
      const r = await axiosInstance.post(`/orders/${orderId}/confirm`, body);
      toast.success(`Pedido confirmado (total ₡${(r.data.total || 0).toLocaleString()})`);
      setSelectedOrder(null);
      fetchOrders();
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al confirmar"));
    } finally {
      setActionId(null);
    }
  };

  const reopenOrder = async (orderId) => {
    if (!window.confirm("¿Reabrir pedido cancelado? Volverá a estado pendiente para poder facturar.")) return;
    setActionId(orderId);
    try {
      await axiosInstance.post(`/orders/${orderId}/reopen`);
      toast.success("Pedido reabierto. Ahora puedes confirmarlo.");
      setSelectedOrder(null);
      fetchOrders();
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al reabrir"));
    } finally {
      setActionId(null);
    }
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm("¿Cancelar pedido?")) return;
    setActionId(orderId);
    try {
      await axiosInstance.post(`/orders/${orderId}/cancel`);
      toast.success("Pedido cancelado");
      setSelectedOrder(null);
      fetchOrders();
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al cancelar"));
    } finally {
      setActionId(null);
    }
  };

  // MASTER-only: change order status freely. When moving away from `confirmed`,
  // the backend will rollback the linked sale and restore stock.
  const masterChangeStatus = async (orderId, newStatus) => {
    const current = selectedOrder?.status;
    if (current === newStatus) return;
    const warn = current === "confirmed"
      ? `Vas a revertir un pedido CONFIRMADO a "${newStatus}". Esto eliminará la venta vinculada y devolverá el stock al inventario. ¿Continuar?`
      : `¿Cambiar estado a "${newStatus}"?`;
    if (!window.confirm(warn)) return;
    setActionId(orderId);
    try {
      await axiosInstance.post(`/orders/${orderId}/status`, { status: newStatus });
      toast.success(`Estado actualizado a "${newStatus}"`);
      setSelectedOrder(null);
      fetchOrders();
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al cambiar estado"));
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="p-4 space-y-4" data-testid="orders-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" /> Pedidos en línea
            {pendingCount > 0 && (
              <span className="ml-2 bg-amber-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                {pendingCount} pendiente{pendingCount > 1 ? "s" : ""}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2 flex-wrap items-center">
            <Select
              value={selectedBox?.id || ""}
              onValueChange={(v) => setSelectedBox(boxes.find((b) => b.id === v))}
            >
              <SelectTrigger className="w-60" data-testid="box-select">
                <SelectValue placeholder="Selecciona caja" />
              </SelectTrigger>
              <SelectContent>
                {boxes.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Tabs value={statusFilter} onValueChange={setStatusFilter}>
              <TabsList>
                <TabsTrigger value="all">Todos</TabsTrigger>
                <TabsTrigger value="pending">Pendientes</TabsTrigger>
                <TabsTrigger value="confirmed">Confirmados</TabsTrigger>
                <TabsTrigger value="cancelled">Cancelados</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {loading ? (
            <div className="text-center py-8 text-slate-500">Cargando...</div>
          ) : orders.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Package className="mx-auto w-12 h-12 mb-2 text-slate-300" />
              <div>No hay pedidos {statusFilter !== "all" ? `en estado "${statusFilter}"` : ""}.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="p-2 text-left">Pedido</th>
                    <th className="p-2 text-left">Fecha</th>
                    <th className="p-2 text-left">Cliente</th>
                    <th className="p-2 text-left">Entrega</th>
                    <th className="p-2 text-right">Total</th>
                    <th className="p-2 text-left">Estado</th>
                    <th className="p-2 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => {
                    const isSent = o.status === "pending" && o.sent_to_cashier_at;
                    const s = isSent
                      ? { txt: "Por facturar", color: "bg-blue-100 text-blue-800 border-blue-300", icon: Clock }
                      : (STATUS_LABEL[o.status] || STATUS_LABEL.pending);
                    const Icon = s.icon;
                    return (
                      <tr key={o.id} className="border-b hover:bg-slate-50" data-testid={`order-row-${o.id}`}>
                        <td className="p-2 font-mono text-xs">{o.order_number}</td>
                        <td className="p-2 text-xs">{(o.created_at || "").replace("T", " ").substring(0, 16)}</td>
                        <td className="p-2">
                          <div className="font-semibold">{o.customer_name}</div>
                          <div className="text-xs text-slate-500">{o.customer_phone}</div>
                        </td>
                        <td className="p-2 text-xs">
                          {o.delivery_method === "delivery" ? "🚚 Envío" : "🏪 Retiro"}
                        </td>
                        <td className="p-2 text-right font-bold">{fmt(o.total)}</td>
                        <td className="p-2">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${s.color}`}>
                            <Icon className="w-3 h-3" /> {s.txt}
                          </span>
                        </td>
                        <td className="p-2 text-center">
                          <Button size="sm" variant="outline" onClick={() => setSelectedOrder(o)} data-testid={`view-order-${o.id}`}>
                            Ver
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={(o) => !o && setSelectedOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedOrder && (
            <>
              <DialogHeader>
                <DialogTitle>Pedido {selectedOrder.order_number}</DialogTitle>
                <DialogDescription>
                  {(selectedOrder.created_at || "").replace("T", " ").substring(0, 19)}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="bg-slate-50 rounded-md p-3 space-y-1">
                  <div className="font-bold flex items-center gap-1"><Phone className="w-3 h-3" /> {selectedOrder.customer_name}</div>
                  <div className="text-slate-600">{selectedOrder.customer_phone}</div>
                  {selectedOrder.customer_email && <div className="text-slate-600 flex items-center gap-1"><Mail className="w-3 h-3" /> {selectedOrder.customer_email}</div>}
                  <div className="flex items-center gap-1 text-slate-700 pt-1">
                    {selectedOrder.delivery_method === "delivery" ? <Truck className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
                    <strong>{selectedOrder.delivery_method === "delivery" ? "Envío" : "Retiro en tienda"}</strong>
                  </div>
                  {selectedOrder.delivery_address && <div className="text-slate-600 pl-5">{selectedOrder.delivery_address}</div>}
                  <div className="text-slate-700"><strong>Pago:</strong> {selectedOrder.payment_intent}</div>
                  {selectedOrder.notes && <div className="text-slate-700"><strong>Notas:</strong> {selectedOrder.notes}</div>}
                </div>

                <div>
                  <div className="font-bold mb-1">Productos:</div>
                  <table className="w-full text-xs border">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="p-1 text-left">Código</th>
                        <th className="p-1 text-left">Producto</th>
                        <th className="p-1 text-right">Cant</th>
                        <th className="p-1 text-right">Precio</th>
                        <th className="p-1 text-right">Sub</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedOrder.items || []).map((it, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-1 font-mono">{it.product_code}</td>
                          <td className="p-1">
                            {it.product_name}
                            {(it.color || it.talla) && (
                              <span className="text-slate-400 text-[10px] ml-1">
                                {it.color && `· ${it.color}`} {it.talla && `· T:${it.talla}`}
                              </span>
                            )}
                          </td>
                          <td className="p-1 text-right">{it.quantity}</td>
                          <td className="p-1 text-right">{fmt(it.price)}</td>
                          <td className="p-1 text-right font-bold">{fmt(it.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-100 font-bold">
                        <td colSpan={4} className="p-1 text-right">TOTAL:</td>
                        <td className="p-1 text-right text-emerald-700">{fmt(selectedOrder.total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {selectedOrder.status === "pending" && selectedOrder.sent_to_cashier_at && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-2 text-blue-800 text-xs" data-testid="sent-to-cashier-banner">
                    Pendiente por facturar · enviado a caja por <strong>{selectedOrder.sent_to_cashier_by}</strong> el {(selectedOrder.sent_to_cashier_at || "").substring(0, 19).replace("T", " ")}
                    {(selectedOrder.pending_discount_percentage > 0 || selectedOrder.pending_discount_amount > 0) && (
                      <div className="mt-1">
                        Descuento pre-cargado:{" "}
                        <strong>
                          {selectedOrder.pending_discount_percentage > 0
                            ? `${selectedOrder.pending_discount_percentage}%`
                            : `₡${(selectedOrder.pending_discount_amount || 0).toLocaleString()}`}
                        </strong>
                      </div>
                    )}
                  </div>
                )}
                {selectedOrder.status === "confirmed" && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2 text-emerald-800 text-xs">
                    Confirmado por <strong>{selectedOrder.confirmed_by}</strong> el {(selectedOrder.confirmed_at || "").substring(0, 19).replace("T", " ")}. Venta: <code>{selectedOrder.sale_id}</code>
                  </div>
                )}
                {selectedOrder.status === "cancelled" && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-2 text-red-800 text-xs">
                    Cancelado por <strong>{selectedOrder.cancelled_by}</strong> el {(selectedOrder.cancelled_at || "").substring(0, 19).replace("T", " ")}
                  </div>
                )}

                {user?.role === "master" && (
                  <div className="border border-purple-200 bg-purple-50 rounded-md p-2 mt-1">
                    <div className="text-xs font-bold text-purple-800 mb-1 flex items-center gap-1">
                      <span className="inline-block w-2 h-2 rounded-full bg-purple-600"></span> Control MASTER · Cambiar estado del pedido
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                      <Select
                        value={selectedOrder.status}
                        onValueChange={(v) => masterChangeStatus(selectedOrder.id, v)}
                      >
                        <SelectTrigger className="w-44 h-8 text-xs bg-white" data-testid="master-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value} data-testid={`master-status-opt-${opt.value}`}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="text-[10px] text-purple-700">
                        Revertir un pedido confirmado devuelve el stock y elimina la venta.
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                {selectedOrder.customer_phone && (() => {
                  const url = buildWaUrl(selectedOrder);
                  if (!url) return null;
                  return (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-2 mr-auto rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition"
                      data-testid="notify-whatsapp-btn"
                      title="Abrir WhatsApp con mensaje pre-cargado"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Notificar por WhatsApp
                    </a>
                  );
                })()}
                {selectedOrder.status === "pending" && !selectedOrder.sent_to_cashier_at && (
                  <>
                    <div className="w-full mb-2 rounded-md border border-emerald-200 bg-emerald-50 p-2">
                      <div className="text-xs font-bold text-emerald-700 mb-1">Aplicar descuento (opcional)</div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-slate-600">% descuento</label>
                          <input
                            type="number" min="0" max="100" step="0.5"
                            value={discountPct}
                            onChange={(e) => { setDiscountPct(e.target.value); if (e.target.value) setDiscountAmount(""); }}
                            placeholder="0"
                            className="w-full border rounded px-2 py-1 text-sm"
                            data-testid="discount-pct-input"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-600">o monto ₡</label>
                          <input
                            type="number" min="0" step="100"
                            value={discountAmount}
                            onChange={(e) => { setDiscountAmount(e.target.value); if (e.target.value) setDiscountPct(""); }}
                            placeholder="0"
                            className="w-full border rounded px-2 py-1 text-sm"
                            data-testid="discount-amount-input"
                          />
                        </div>
                      </div>
                      {(parseFloat(discountPct) > 0 || parseFloat(discountAmount) > 0) && (() => {
                        const t = parseFloat(selectedOrder.total) || 0;
                        const dAmt = parseFloat(discountAmount) > 0 ? parseFloat(discountAmount) : (t * parseFloat(discountPct) / 100);
                        return <div className="text-xs text-emerald-800 mt-1">Total con descuento: <strong>₡{Math.max(0, t - dAmt).toLocaleString()}</strong></div>;
                      })()}
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => cancelOrder(selectedOrder.id)}
                      disabled={actionId === selectedOrder.id}
                      className="border-red-300 text-red-700 hover:bg-red-50"
                      data-testid="cancel-order-btn"
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Cancelar
                    </Button>
                    <Button
                      onClick={() => sendToInventory(selectedOrder)}
                      disabled={actionId === selectedOrder.id}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="confirm-order-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" /> Enviar a caja para facturar
                    </Button>
                  </>
                )}
                {selectedOrder.status === "pending" && selectedOrder.sent_to_cashier_at && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => recallFromCashier(selectedOrder.id)}
                      disabled={actionId === selectedOrder.id}
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="recall-order-btn"
                    >
                      Recuperar del carrito
                    </Button>
                    <Button
                      onClick={() => { window.location.href = "/inventory"; }}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="goto-cashier-btn"
                    >
                      Ir a caja a facturar →
                    </Button>
                  </>
                )}
                {selectedOrder.status === "cancelled" && user?.role === "master" && (
                  <Button
                    onClick={async () => { await reopenOrder(selectedOrder.id); sendToInventory({ ...selectedOrder, status: "pending" }); }}
                    disabled={actionId === selectedOrder.id}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="reopen-order-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Reabrir y enviar a caja
                  </Button>
                )}
                {(selectedOrder.status !== "pending" || selectedOrder.sent_to_cashier_at) && (
                  <Button variant="outline" onClick={() => setSelectedOrder(null)}>Cerrar</Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
